<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <header class="relative flex h-16 shrink-0 items-center gap-2 border-b border-default bg-default px-3 sm:px-5">
        <UButton
            :aria-label="$t('nav.menu')"
            class="lg:hidden"
            color="neutral"
            variant="ghost"
            icon="i-lucide-menu"
            size="lg"
            @click="toggleMobileMenu"
        />

        <span class="truncate text-base font-semibold text-highlighted lg:hidden">{{ $t('app.name') }}</span>

        <!-- The scope sits at the LEFT and not among the account buttons on the right:
             it is the first thing every screen under it is a function of, not a
             preference. One instance and not one per breakpoint — two would be two
             controls claiming the same name to a test and to a screen reader. -->
        <ScopeSelector />

        <div class="ms-auto flex items-center gap-2">
            <span v-if="userName" class="hidden max-w-40 truncate text-sm text-muted lg:inline">
                {{ userName }}
            </span>

            <!-- Three states, one button: Light → Dark → System → Light. The current one
                 is named on the button itself and not only in a tooltip. -->
            <UButton
                :label="themeLabel"
                :aria-label="`${$t('nav.theme')}: ${themeLabel}`"
                :title="`${$t('nav.theme')}: ${themeLabel}`"
                :icon="themeIcon"
                :ui="{ label: 'hidden lg:inline' }"
                color="neutral"
                variant="ghost"
                size="lg"
                @click="cycleTheme"
            />

            <UButton
                :label="languageLabel"
                :aria-label="`${$t('nav.language')}: ${languageName}`"
                :title="`${$t('nav.language')}: ${languageName}`"
                :ui="{ label: 'font-semibold' }"
                color="neutral"
                variant="ghost"
                icon="i-lucide-languages"
                size="lg"
                @click="cycleLanguage"
            />

            <UButton
                :label="$t('nav.logout')"
                :aria-label="$t('nav.logout')"
                :ui="{ label: 'hidden lg:inline' }"
                color="neutral"
                variant="ghost"
                icon="i-lucide-log-out"
                size="lg"
                @click="logout"
            />
        </div>

        <!-- Non-blocking progress hint: the screen stays readable while a request is in
             flight, so loading never takes the page away. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="absolute inset-x-0 bottom-0 h-0.5 animate-pulse bg-primary"
        />
    </header>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import ScopeSelector from '@/layouts/components/ScopeSelector.vue'

    import { useLayout } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'

    const { t }    = useI18n()
    const router   = useRouter()
    const appStore = useAppStore()

    const { toggleMobileMenu, closeMobileMenu } = useLayout()

    const userName = computed(() => appStore.userData?.name ?? appStore.userData?.username ?? '')

    const themeOrder = ['light', 'dark', 'auto'] as const

    const themeIcon = computed(() => {
        if (appStore.colorMode === 'light') return 'i-lucide-sun'
        if (appStore.colorMode === 'dark')  return 'i-lucide-moon'

        return 'i-lucide-monitor'
    })

    const themeLabel = computed(() => {
        if (appStore.colorMode === 'light') return t('nav.theme_light')
        if (appStore.colorMode === 'dark')  return t('nav.theme_dark')

        return t('nav.theme_system')
    })

    const cycleTheme = () => {
        const current = themeOrder.indexOf(appStore.colorMode as typeof themeOrder[number])

        appStore.colorMode = themeOrder[(current + 1) % themeOrder.length]
    }

    // Two languages, so one button that swaps them rather than a select to open. The
    // button shows the code it is on now; the full name is on the tooltip.
    const languages = ['sl', 'en'] as const

    const languageLabel = computed(() => appStore.locale.toUpperCase())

    const languageName = computed(() =>
        appStore.locale === 'en' ? t('nav.language_en') : t('nav.language_sl'))

    const cycleLanguage = () => {
        const current = languages.indexOf(appStore.locale as typeof languages[number])

        appStore.changeLocale(languages[(current + 1) % languages.length])
    }

    const logout = () => {
        closeMobileMenu()
        appStore.setLogout()
        router.push({ name: 'login' })
    }
</script>
